/**
 * App Tour state. Progress (lessons done) is saved per user on the server:
 * GET /tour/state once per signed-in user, POST /tour/progress on every tick.
 * The video list comes from the manifest under `videoBaseUrl` (see tourVideos.ts).
 * The switches (platform, language, scope) and the running walk live only here.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { api } from '@/utils/api';
import type { TourPlatform, TourScope } from './tourCatalog';
import { fetchVideoManifest } from './tourVideos';

export type TourLanguage = 'hi' | 'en';
export interface TourWalkStep {
  /** CSS selector of the real element to keep bright. Missing or not found → the card shows without a ring. */
  selector?: string;
  /** Page this step runs on. Missing → stay on the page the walk is on. */
  route?: string;
  /** A control that opens a form, pressed when the step's element is not on the page yet. Never one that saves or sends. */
  open?: string;
  title: string;
  body: string;
}
export interface TourWalk {
  lessonId: string;
  lessonTitle: string;
  steps: TourWalkStep[];
}

export const SAVE_FAILED = 'Could not save. Try again.';
const LANGUAGE_KEY = 'aura-tour-language';

/** The audio language is remembered for the browser session. Hindi first. */
function readLanguage(): TourLanguage {
  try { return window.sessionStorage.getItem(LANGUAGE_KEY) === 'en' ? 'en' : 'hi'; } catch { return 'hi'; }
}

function idList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
}

interface TourState {
  platform: TourPlatform;
  language: TourLanguage;
  scope: TourScope;
  done: string[];
  /** Whose progress `done` holds, and whether the server answered. */
  loadedFor: string | null;
  status: 'idle' | 'loading' | 'ready' | 'failed';
  promptSeen: boolean;
  videoBaseUrl: string | null;
  /** Files named in the video manifest, e.g. "web/M08-L20-hi.mp4". Empty until it is read, or when there is none. */
  videos: string[];
  /** One-line message shown as a toast (a save that failed). */
  notice: string | null;
  walk: TourWalk | null;
  walkStep: number;
  setPlatform: (platform: TourPlatform) => void;
  setLanguage: (language: TourLanguage) => void;
  setScope: (scope: TourScope) => void;
  loadState: (userId: string) => Promise<void>;
  markDone: (lessonId: string) => Promise<void>;
  clearNotice: () => void;
  startWalk: (walk: TourWalk) => void;
  nextWalkStep: () => void;
  exitWalk: () => void;
}

export const useTourStore = create<TourState>((set, get) => ({
  platform: 'web',
  language: readLanguage(),
  scope: 'all',
  done: [],
  loadedFor: null,
  status: 'idle',
  promptSeen: true,
  videoBaseUrl: null,
  videos: [],
  notice: null,
  walk: null,
  walkStep: 0,
  setPlatform: (platform) => set({ platform }),
  setLanguage: (language) => {
    try { window.sessionStorage.setItem(LANGUAGE_KEY, language); } catch { /* Storage may be unavailable. */ }
    set({ language });
  },
  setScope: (scope) => set({ scope }),
  loadState: async (userId) => {
    if (get().loadedFor === userId && get().status !== 'idle') return;
    set({ loadedFor: userId, status: 'loading', done: [], promptSeen: true, videoBaseUrl: null, videos: [] });
    try {
      const data = (await api.get('/tour/state')).data?.data ?? {};
      if (get().loadedFor !== userId) return;
      // A lesson ticked while the reply was on its way stays ticked.
      const done = [...new Set([...idList(data.done), ...get().done])];
      const videoBaseUrl = typeof data.videoBaseUrl === 'string' && data.videoBaseUrl ? data.videoBaseUrl.replace(/\/+$/, '') : null;
      set({ status: 'ready', done, promptSeen: data.promptSeen === true, videoBaseUrl });
      const videos = await fetchVideoManifest(videoBaseUrl);
      if (get().loadedFor === userId) set({ videos });
    } catch {
      // The tour still opens: nothing done, no videos.
      if (get().loadedFor === userId) set({ status: 'failed' });
    }
  },
  markDone: async (lessonId) => {
    if (get().done.includes(lessonId)) return;
    set({ done: [...get().done, lessonId] });
    try {
      await api.post('/tour/progress', { lessonId, done: true });
    } catch {
      set({ done: get().done.filter((id) => id !== lessonId), notice: SAVE_FAILED });
    }
  },
  clearNotice: () => set({ notice: null }),
  startWalk: (walk) => set({ walk, walkStep: 0 }),
  nextWalkStep: () => set((state) => ({ walkStep: state.walkStep + 1 })),
  exitWalk: () => set({ walk: null, walkStep: 0 }),
}));

/** Loads the signed-in user's tour state once. Call it from every tour page. */
export function useLoadTourState(userId: string | null | undefined) {
  const loadState = useTourStore((s) => s.loadState);
  useEffect(() => { if (userId) void loadState(userId); }, [userId, loadState]);
}
